// THE FIELD AS THE ACTOR: the harvesting looks that draw on the deposit
// itself (R1120, mockup round 055 group d).
//
// One entry per look id of `labels.toml gather_styles` that this file owns:
// `drain-pulse` (card 16) and `spiral-bit` (card 20). `gather-styles.ts`
// prefers this map over the AUTO look, so a look that is not here yet draws
// the AUTO look and the map fills in one card at a time.
//
// **PENDING names every card of this file that is not built yet.** An id must
// stand in `RECIPES` or in `PENDING`, and `gather-recipes-field.test.ts`
// fails when one stands in neither.
//
// **`drain-pulse` draws OVER the field mark and never replaces it** (R1124).
// The mark is the reader's own Marks pick of six (R832), and the atlas bakes
// two of the six with a piece no recipe can draw: the `ghost` halo is a
// radial ramp and the `tag` letter is text. The recipe therefore leaves
// `drawsField` unset, reads `c.deposit`, and holds under all six:
//
//   `drain` `strike` `gauge` `ghost`  the diamond, so the shimmer on the
//                                     level line and the wet rim below it
//   `tag` `tally`                     no diamond, so the beat, the echo and
//                                     the step flash stand on the mark's own
//                                     outline and no level line draws
//
// **`drain-pulse` draws once per FIELD.** It tests `c.fieldLead`, so a stack
// of Dredgers on one deposit gives one mark and not five (R1124).
//
// **A recipe reads its figures out of the row and holds no literal.** A
// PROPORTION is not a figure: how far a beat swells a stroke, which share of
// an arm runs bright, how a fade is shaped — that stays here as a module
// constant, the way `FLASH_CORE_FLOOR` does in the combat kit. The row
// carries four alphas and two strokes, and each card names more than that.
//
// This is a leaf (`leaves.test.ts`): it imports the kit and the generated
// pack, and never an engine.

import { DEPOSIT_MARKS } from '../gen/presentation';
import {
  TAU, DEPOSIT_DIAMOND, DEPOSIT_HALF_Y, GATHER_WHITE,
  box, clamp01, diamond, diamondHalf, lerp, levelY, lighten, line, lotBeat,
  onEnv, polyline, rect, ring,
  type GatherCtx, type GatherRecipe,
} from './gather-recipe-kit';

// ---------------------------------------------------------------------------
// The mark under the look (R1124)
// ---------------------------------------------------------------------------
//
// `drain-pulse` beats on the mark the reader picked, so these four answer the
// mark's own geometry. A value this build does not know falls back to the
// diamond, which is what every look resolver does (themes.md §12).

/** True where the picked deposit mark carries the diamond of R475. */
function hasDiamond(c: GatherCtx): boolean {
  return c.deposit !== 'tag' && c.deposit !== 'tally';
}

/** The mark's own half height, in the field's space. */
function markHalfY(c: GatherCtx): number {
  if (hasDiamond(c)) return DEPOSIT_HALF_Y;
  return c.deposit === 'tag'
    ? DEPOSIT_MARKS.tag.size / 2
    : DEPOSIT_MARKS.tally.barH / 2;
}

/**
 * How wide the mark stands at the height `y`, in the field's space. A height
 * above or below the mark answers 0, so no line draws there (R3011).
 */
function markHalfAt(c: GatherCtx, y: number): number {
  if (hasDiamond(c)) return diamondHalf(y);
  if (Math.abs(y) > markHalfY(c)) return 0;
  if (c.deposit === 'tag') return DEPOSIT_MARKS.tag.size / 2;
  // The bars of `tally` stand side by side, and the count is the commodity.
  const f = DEPOSIT_MARKS.tally;
  return (c.chips * f.barW + (c.chips - 1) * f.gap) / 2;
}

/**
 * The mark's own outline, scaled by `k` about the field.
 *
 * It is the card's `PF(c, DIAMOND); S(…)` under a `scale(k, k)`, and the two
 * marks with no diamond answer their own shape instead.
 */
function markOutline(
  c: GatherCtx, k: number, w: number, ink: number, a: number,
): void {
  if (hasDiamond(c)) {
    diamond(c, c.fx, c.fy, k, w, ink, a);
    return;
  }
  if (c.deposit === 'tag') {
    const s = DEPOSIT_MARKS.tag.size * k;
    box(c, c.fx, c.fy, s, s, 0, w, ink, a);
    return;
  }
  const f = DEPOSIT_MARKS.tally;
  const pitch = (f.barW + f.gap) * k;
  const x0 = c.fx - ((c.chips - 1) * pitch) / 2;
  for (let i = 0; i < c.chips; i++) {
    box(c, x0 + i * pitch, c.fy, f.barW * k, f.barH * k, 0, w, ink, a);
  }
}

/**
 * The part of the mark's outline at or below the level line `ly`.
 *
 * The card clips the rim to `rect(−8, ly, 16, 16)`, and the batches carry no
 * clip, so each edge of the diamond is cut at the level line instead. A mark
 * with no diamond has no level line, so its WHOLE outline beats.
 */
function rimBelow(
  c: GatherCtx, ly: number, w: number, ink: number, a: number,
): void {
  if (!hasDiamond(c)) {
    markOutline(c, 1, w, ink, a);
    return;
  }
  const p = DEPOSIT_DIAMOND;
  const n = p.length >> 1;
  for (let i = 0; i < n; i++) {
    const j = (i + 1) % n;
    let x0 = p[i * 2], y0 = p[i * 2 + 1];
    let x1 = p[j * 2], y1 = p[j * 2 + 1];
    if (y0 < ly && y1 < ly) continue;
    if (y0 < ly) {
      const t = (ly - y0) / (y1 - y0);
      x0 = lerp(x0, x1, t);
      y0 = ly;
    } else if (y1 < ly) {
      const t = (ly - y0) / (y1 - y0);
      x1 = lerp(x0, x1, t);
      y1 = ly;
    }
    // An edge the level line only TOUCHES leaves nothing behind. The segment
    // sink caps a line with a round end, so a segment of no length would read
    // as a dot on the rim.
    if (x0 === x1 && y0 === y1) continue;
    line(c, c.fx + x0, c.fy + y0, c.fx + x1, c.fy + y1, w, ink, a);
  }
}

// ---------------------------------------------------------------------------
// The proportions
// ---------------------------------------------------------------------------

/** drain-pulse, card 16: how far the bright twin of the commodity hue stands
 *  from the hue itself. The card writes `lighten(sc.res, 0.7)`, which is
 *  twice the lift `c.resHi` carries. */
const DRAIN_HI = 0.7;

/** drain-pulse: the wet rim at rest, as a share of the cut. The card writes
 *  `0.2 * w + 0.6 * beat`, and the row holds the beat's own share. */
const DRAIN_RIM_REST = 0.2;
/** drain-pulse: how much one lot's beat swells the wet rim, in world px. */
const DRAIN_RIM_SWELL = 0.6;

/** drain-pulse: what the cut adds to the shimmer's alpha. The card writes
 *  `0.55 + 0.4 * w`, and the row holds the figure it rests at. */
const DRAIN_WORK_LIFT = 0.4;
/** drain-pulse: the flat line of a full hold, in the cargo brown. */
const DRAIN_FULL_ALPHA = 0.95;
/** drain-pulse: the bright block one lot lays on the level line. */
const DRAIN_BLOCK_ALPHA = 0.22;

/** drain-pulse: under which beat the echo of a lot does not draw at all. */
const DRAIN_ECHO_FLOOR = 0.02;
/** drain-pulse: how far the echo of a lot opens over its own lot, and the
 *  stroke it opens at. A ring that grows keeps one WEIGHT, so the stroke is
 *  divided by the scale, which is what the card's `0.9 / k` does. */
const DRAIN_ECHO_GROW = 0.3;
const DRAIN_ECHO_W = 0.9;

/** drain-pulse: how far the flash of a drain step opens, the stroke it opens
 *  at, and the alpha of that second ring. The row's `alpha3` carries the
 *  white flash on the rim itself. */
const DRAIN_STEP_GROW = 0.7;
const DRAIN_STEP_W = 1.1;
const DRAIN_STEP_ECHO_ALPHA = 0.6;

/** drain-pulse: the shaft's scan line. The card names two strokes and two
 *  alphas for it, and the row holds one of each. `REST` is the share the
 *  leading line keeps at the top and the foot of its walk. */
const DRAIN_SCAN_W = 1;
const DRAIN_SCAN_REST = 0.35;
const DRAIN_SCAN_TRAIL_W = 0.8;
const DRAIN_SCAN_TRAIL_ALPHA = 0.25;

/** drain-pulse: how many points the shimmer may be walked as. The row states
 *  the count and this is the scratch, which is allocated once (R131). */
const SHIMMER_CAP = 64;
const SHIMMER_X = new Float32Array(SHIMMER_CAP);
const SHIMMER_Y = new Float32Array(SHIMMER_CAP);

/** spiral-bit, card 20: how many turns one arm walks from the bore to the
 *  rim. It is the shape of the spiral and not a figure a designer moves. */
const SPIRAL_TURNS = 1.05;
/** spiral-bit: which share of an arm runs at the full alpha. The outer part
 *  runs at `SPIRAL_OUTER` of it, so the laden ring stays the one firm
 *  circle. */
const SPIRAL_INNER = 0.6;
const SPIRAL_OUTER = 0.5;
/** spiral-bit: the spiral's turn in the SHAFT, in radians a second. The row
 *  holds one speed and the card names two, and the shaft's is not a share of
 *  the gather's: the card slows the bit rather than paying the shaft rate. */
const SPIRAL_SHAFT_SPIN = 0.45;
/** spiral-bit: how long the arm head runs, as a share of one arm, in the
 *  gather and in the shaft. */
const SPIRAL_HEAD_SPAN = 0.1;
const SPIRAL_HEAD_SPAN_SHAFT = 0.16;
/** spiral-bit: the arm head's stroke in the SHAFT, in world px. */
const SPIRAL_HEAD_W_SHAFT = 1.2;
/** spiral-bit: how far the arm head's twin stands from the commodity hue. */
const SPIRAL_HEAD_HI = 0.6;
/** spiral-bit: how fast the arm head rises at the start of its lot, and the
 *  share it is already lit at. The card writes `min(1, 5 * lot + 0.2)`. */
const SPIRAL_HEAD_RISE = 5;
const SPIRAL_HEAD_FLOOR = 0.2;
/** spiral-bit: how many points the arm head is walked as. */
const SPIRAL_HEAD_STEPS = 5;
/** spiral-bit: the dash of a SHAFT arm, in world px. `dashOn`/`dashOff` take
 *  the card's `[2.2, 2]` straight. */
const SPIRAL_DASH_ON = 2.2, SPIRAL_DASH_OFF = 2;

/** spiral-bit: how many points one arm may be walked as, and the scratch it
 *  is walked into (R131). */
const SPIRAL_CAP = 64;
const SPIRAL_X = new Float32Array(SPIRAL_CAP);
const SPIRAL_Y = new Float32Array(SPIRAL_CAP);

/** One point of a spiral arm at `u`, from the bore to the rim. */
function armAt(
  c: GatherCtx, u: number, arm: number, rot: number, r0: number, r1: number,
  out: { x: number; y: number },
): void {
  const a = rot + arm * Math.PI + u * SPIRAL_TURNS * TAU;
  const r = lerp(r0, r1, u);
  out.x = c.fx + Math.cos(a) * r;
  out.y = c.fy + Math.sin(a) * r;
}

/** The reused point of the spiral walk (R131). */
const ARM_PT = { x: 0, y: 0 };

/**
 * A run of one arm, from the point `i0` to the point `i1` of `n` steps.
 *
 * The run is walked into the scratch and drawn one segment a step, each
 * with the dash the caller named, so a dashed SHAFT arm restarts its dash at
 * each segment — the way `ellipse` draws the dashed loop of card 07, because
 * the segment sink has no dash phase.
 */
function armRun(
  c: GatherCtx, arm: number, rot: number, r0: number, r1: number,
  n: number, i0: number, i1: number,
  w: number, ink: number, a: number, dashOn: number, dashOff: number,
): void {
  const count = i1 - i0 + 1;
  if (count < 2 || count > SPIRAL_CAP) return;
  for (let i = 0; i < count; i++) {
    armAt(c, (i0 + i) / n, arm, rot, r0, r1, ARM_PT);
    SPIRAL_X[i] = ARM_PT.x;
    SPIRAL_Y[i] = ARM_PT.y;
  }
  for (let i = 1; i < count; i++) {
    line(c, SPIRAL_X[i - 1], SPIRAL_Y[i - 1], SPIRAL_X[i], SPIRAL_Y[i],
      w, ink, a, dashOn, dashOff);
  }
}

// ---------------------------------------------------------------------------
// The recipes
// ---------------------------------------------------------------------------

export const RECIPES: Readonly<Record<string, GatherRecipe>> = {
  /**
   * Card 16, DRAIN PULSE.
   *
   * The field mark shows the work. A bright line ripples on the level line of
   * the diamond, the rim below it beats with each lot, and the rim flashes on
   * a drain step. A full hold lays the line flat and turns it to the cargo
   * brown. The shaft shows one slow scan line that walks down the mark.
   *
   * It draws over the reader's own deposit mark and once per field (R1124).
   */
  'drain-pulse': {
    mid(c: GatherCtx): void {
      if (!c.fieldLead) return;
      const on = onEnv(c);
      if (on <= 0) return;
      const r = c.row;
      const hi = lighten(c.res, DRAIN_HI);

      if (c.shaft) {
        // One slow scan line for each lot, from the top of the mark to the
        // foot of it, with a faint second line trailing it.
        const half = markHalfY(c);
        const y = -half + 2 * half * c.lot;
        const swell = Math.sin(Math.PI * c.lot);
        const hw = markHalfAt(c, y);
        if (hw > 0) {
          line(c, c.fx - hw, c.fy + y, c.fx + hw, c.fy + y, DRAIN_SCAN_W, hi,
            r.alpha3 * on * (DRAIN_SCAN_REST + (1 - DRAIN_SCAN_REST) * swell));
        }
        const y2 = y - r.driftPx;
        const hw2 = markHalfAt(c, y2);
        if (hw2 > 0) {
          line(c, c.fx - hw2, c.fy + y2, c.fx + hw2, c.fy + y2,
            DRAIN_SCAN_TRAIL_W, hi, DRAIN_SCAN_TRAIL_ALPHA * on * swell);
        }
        return;
      }

      const w = c.work;
      const beat = lotBeat(c) * w;
      const level = hasDiamond(c);
      // The level line, and the wet rim below it. A mark with no diamond has
      // no level line, so its whole outline beats instead (R1124).
      const ly = level ? levelY(c.level) : 0;
      rimBelow(c, ly, r.widthPx + DRAIN_RIM_SWELL * beat, GATHER_WHITE,
        DRAIN_RIM_REST * w + r.alpha2 * beat);

      // The shimmer: it ripples while the cut runs and lies flat once the
      // hold is full. The card clips it to the diamond, so it is walked over
      // the diamond's own span at the level line. A field at the top or the
      // foot of its drain has no span left, and draws no line.
      const span = level ? diamondHalf(ly) : 0;
      if (span > 0) {
        const n = Math.min(SHIMMER_CAP, Math.max(2, r.count));
        const wave = r.rPx > 0 ? TAU / r.rPx : 0;
        for (let i = 0; i < n; i++) {
          const x = lerp(-span, span, i / (n - 1));
          SHIMMER_X[i] = c.fx + x;
          SHIMMER_Y[i] = c.fy + ly
            + r.lengthPx * w * Math.sin(x * wave - c.tw * r.speed);
        }
        const full = c.full > 0;
        polyline(c, SHIMMER_X, SHIMMER_Y, n,
          full ? r.width2Px : r.widthPx,
          full ? c.cargo : GATHER_WHITE,
          full ? DRAIN_FULL_ALPHA : r.alpha + DRAIN_WORK_LIFT * w);
        // The lot's own bright block, below the level line and inside the
        // diamond.
        const by = ly + r.length2Px / 2;
        const bw = diamondHalf(by);
        if (bw > 0) {
          rect(c, c.fx, c.fy + by, 2 * bw, r.length2Px, 0, GATHER_WHITE,
            DRAIN_BLOCK_ALPHA * beat);
        }
      }

      // The echo of the lot, and the larger flash of the rim on a drain step.
      if (beat > DRAIN_ECHO_FLOOR) {
        const k = 1 + DRAIN_ECHO_GROW * c.lot;
        markOutline(c, k, DRAIN_ECHO_W / k, hi, r.alpha4 * beat);
      }
      if (c.step > 0) {
        markOutline(c, 1, r.width2Px, GATHER_WHITE, r.alpha3 * c.step);
        const k = 1 + DRAIN_STEP_GROW * (1 - c.step);
        markOutline(c, k, DRAIN_STEP_W / k, hi, DRAIN_STEP_ECHO_ALPHA * c.step);
      }
    },
  },

  /**
   * Card 20, SPIRAL BIT.
   *
   * A spiral of two thin arms lies under the hull and turns, so the arms run
   * in toward the field. It wears the commodity hue at a low alpha, and a
   * bright arm head runs from the rim to the bore with each lot. A full hold
   * stops the spiral and it fades. The shaft is the special case: there the
   * spiral is tight, slow and dashed, and a hollow ring marks the bore.
   */
  'spiral-bit': {
    mid(c: GatherCtx): void {
      if (!c.atRest) return;
      const on = Math.max(c.work, c.full) * onEnv(c);
      if (on <= 0) return;
      const r = c.row;
      const n = Math.min(SPIRAL_CAP - 1, Math.max(2, r.count));
      const arms = r.count2;
      if (arms < 1) return;
      const r0 = c.shaft ? r.lengthPx : r.rPx;
      const r1 = c.shaft ? r.length2Px : r.r2Px;
      // The turn stands still once the cut stops, because `tw` does.
      const rot = c.tw * (c.shaft ? SPIRAL_SHAFT_SPIN : r.speed) + c.seed;
      const ink = (c.shaft ? r.alpha2 : r.alpha + r.alpha4 * c.step) * on;
      const dashOn = c.shaft ? SPIRAL_DASH_ON : 0;
      const dashOff = c.shaft ? SPIRAL_DASH_OFF : 0;
      const mid = Math.round(n * SPIRAL_INNER);
      for (let arm = 0; arm < arms; arm++) {
        armRun(c, arm, rot, r0, r1, n, 0, mid,
          r.widthPx, c.res, ink, dashOn, dashOff);
        armRun(c, arm, rot, r0, r1, n, mid, n,
          r.widthPx, c.res, ink * SPIRAL_OUTER, dashOn, dashOff);
      }

      // The arm head: it runs from the rim to the bore once for each lot.
      const head = 1 - c.lot;
      const span = c.shaft ? SPIRAL_HEAD_SPAN_SHAFT : SPIRAL_HEAD_SPAN;
      const headInk = lighten(c.res, SPIRAL_HEAD_HI);
      const headA = r.alpha3 * c.work
        * Math.min(1, SPIRAL_HEAD_RISE * c.lot + SPIRAL_HEAD_FLOOR);
      const headW = c.shaft ? SPIRAL_HEAD_W_SHAFT : r.width2Px;
      if (headA > 0) {
        for (let arm = 0; arm < arms; arm++) {
          for (let i = 0; i <= SPIRAL_HEAD_STEPS; i++) {
            const u = clamp01(head + (span * i) / SPIRAL_HEAD_STEPS);
            armAt(c, u, arm, rot, r0, r1, ARM_PT);
            SPIRAL_X[i] = ARM_PT.x;
            SPIRAL_Y[i] = ARM_PT.y;
          }
          polyline(c, SPIRAL_X, SPIRAL_Y, SPIRAL_HEAD_STEPS + 1,
            headW, headInk, headA);
        }
      }

      // The bore of the shaft: the one piece the gather does not draw.
      if (c.shaft) {
        ring(c, c.fx, c.fy, r.driftPx, r.widthPx, c.resHi,
          r.alpha3 * on);
      }
    },
  },
};

/**
 * The cards of this file that wait for their own recipe.
 *
 * Both cards are built, so the table is empty.
 */
export const PENDING: readonly string[] = [];
